import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  useNotifications,
  selectNotifications,
} from '@/features/notifications/api/use-notifications';
import { useUnreadNotificationCount } from '@/features/notifications/api/use-unread-count';
import { useRealtimeNotifications } from '@/features/notifications/api/use-realtime-notifications';
import { NotificationsDropdown } from './NotificationsDropdown';
import { RealtimeStatusBadge } from './RealtimeStatusBadge';

/**
 * NotificationBell (W6-F5-S2) — topbar bell with unread badge + dropdown of
 * the last 10 notifications + realtime status indicator (S3).
 *
 * **W132-F5** — the badge count was previously derived client-side from
 * the first 10 polled rows, which drifted out of sync with `/notifications`
 * (sidebar shows 1, page shows 0). Now reads from the canonical
 * `useUnreadNotificationCount` hook (Core's `total`) so every surface
 * stays in sync. The dropdown still uses `useNotifications({limit:10})`
 * for the row list — only the badge swaps source.
 */
export function NotificationBell(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const realtime = useRealtimeNotifications();
  const list = useNotifications({ limit: 10 });
  const items = selectNotifications(list.data);
  // W132-F5: badge count comes from the dedicated unread-count query
  // (Core-authoritative total) rather than the limited-list slice.
  const unreadQuery = useUnreadNotificationCount();
  const unreadCount = unreadQuery.data?.total ?? 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('notifications.bell.ariaLabel', { count: unreadCount })}
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 ? (
            <span
              data-testid="bell-badge"
              className={cn(
                'absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white',
              )}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
          <span className="absolute -bottom-0.5 -right-0.5">
            <RealtimeStatusBadge status={realtime.status} lastEventAt={realtime.lastEventAt} />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto p-0">
        <NotificationsDropdown
          items={items}
          isLoading={list.isLoading}
          onClose={() => setOpen(false)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
